'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import { Root, Thumb } from '@radix-ui/react-switch';
import { cva } from 'class-variance-authority';
import type { ComponentPropsWithoutRef } from 'react';
import { forwardRef } from 'react';
const switchRootVariants = cva('relative rounded-pill outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-Gray-black/50 active:opacity-90 data-[state=checked]:bg-Gray-black data-[state=unchecked]:bg-Gray-300', {
  variants: {
    size: {
      small: 'h-24 w-40',
      large: 'h-30 w-50'
    },
    disabled: {
      true: 'cursor-not-allowed opacity-50'
    }
  },
  defaultVariants: {
    size: 'small'
  }
});
const switchThumbVariants = cva('block rounded-pill bg-Gray-white transition-transform duration-200 motion-reduce:transition-none', {
  variants: {
    size: {
      small: 'size-20 data-[state=checked]:translate-x-[18px] data-[state=unchecked]:translate-x-[2px]',
      large: 'size-26 data-[state=checked]:translate-x-[22px] data-[state=unchecked]:translate-x-[2px]'
    }
  }
});
interface SwitchProps extends Omit<ComponentPropsWithoutRef<typeof Root>, 'onChange' | 'onCheckedChange'> {
  checked?: boolean;
  disabled?: boolean;
  size?: 'small' | 'large';
  onChange?: (checked: boolean) => void;
}
export const Switch = forwardRef<HTMLButtonElement, SwitchProps>((t0, ref) => {
  const $ = _c(27);
  let className;
  let onChange;
  let props;
  let t1;
  let t2;
  let t3;
  if ($[0] !== t0) {
    ({
      checked: t1,
      onChange,
      disabled: t2,
      size: t3,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onChange;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    className = $[1];
    onChange = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
  }
  const checked = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const size = t3 === undefined ? "small" : t3;
  let t4;
  if ($[7] !== className) {
    t4 = cn("inline-flex items-center gap-8", className);
    $[7] = className;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== disabled || $[10] !== size) {
    t5 = switchRootVariants({
      size,
      disabled
    });
    $[9] = disabled;
    $[10] = size;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== size) {
    t6 = switchThumbVariants({
      size
    });
    $[12] = size;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <Thumb className={t6} />;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== checked || $[17] !== disabled || $[18] !== onChange || $[19] !== props || $[20] !== ref || $[21] !== t5 || $[22] !== t7) {
    t8 = <Root ref={ref} checked={checked} onCheckedChange={onChange} disabled={disabled} className={t5} {...props}>{t7}</Root>;
    $[16] = checked;
    $[17] = disabled;
    $[18] = onChange;
    $[19] = props;
    $[20] = ref;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t4 || $[25] !== t8) {
    t9 = <div className={t4}>{t8}</div>;
    $[24] = t4;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
});
Switch.displayName = 'Switch';